import { AnalyticsBrowser } from "@segment/analytics-next";
import type { MutableRefObject } from "react";
import { isDevelopment } from "./get_env";
import { SEGMENT_KEY } from "app_constants";
import {
  findBrandGroupByStoreOrBrandKey,
  findStoreByStoreKey,
} from "helpers/brand";
import { getSubscriptionLevel } from "helpers/subscription_level";
import type { IBrandGroup } from "helpers/transforms/entities";

/**
 * A ref to store the U-Series analytics ref.
 * It is not the same analytics instance with LSPay
 */
export const globalAnalyticsRef: MutableRefObject<AnalyticsBrowser | null> = {
  current: null,
};

export const isTrackingEnabled = () => {
  return !isDevelopment();
};

export const initTracking = () => {
  if (!isTrackingEnabled()) {
    return;
  }

  if (globalAnalyticsRef.current) {
    return;
  }

  globalAnalyticsRef.current = new AnalyticsBrowser();
  globalAnalyticsRef.current.load({ writeKey: SEGMENT_KEY });
  globalAnalyticsRef.current.page();
};

function trackEvent(
  event: string,
  properties?: { [name: string]: string | number | boolean }
) {
  if (isTrackingEnabled() && globalAnalyticsRef.current) {
    globalAnalyticsRef.current.track(event, properties);
  } else {
    // eslint-disable-next-line no-console
    console.log("trackEvent: ", event, properties);
  }
}

function generateAnalyticsProperties(
  brandGroup: IBrandGroup | undefined,
  vendorLevel: string,
  vendorLocation: string
): { [name: string]: string | number | boolean } {
  let customReports = false;
  let upserveOlo = false;
  let activePosInstallationName = "";
  const currentSubLevel = getSubscriptionLevel(
    brandGroup,
    vendorLevel,
    vendorLocation
  );
  const subscriptionLevel = currentSubLevel ? currentSubLevel.toString() : "";

  if (vendorLevel === "b") {
    return { subscriptionLevel };
  }
  if (vendorLevel === "s" && brandGroup) {
    const location = findStoreByStoreKey(vendorLocation, [brandGroup]);
    if (location) {
      customReports = location.customReports;
      upserveOlo = location.upserveOlo;
      activePosInstallationName = location.activePosInstallationName;
    }
  }
  return {
    subscriptionLevel,
    customReports,
    upserveOlo,
    activePosInstallationName,
  };
}

function identifyUser(
  name: string,
  email: string,
  vendorLevel: string,
  vendorLocation: string,
  entities: IBrandGroup[]
) {
  if (globalAnalyticsRef.current) {
    const currentBrandGroup = findBrandGroupByStoreOrBrandKey(
      vendorLevel,
      vendorLocation,
      entities
    );
    const analyticsProperties = generateAnalyticsProperties(
      currentBrandGroup,
      vendorLevel,
      vendorLocation
    );
    if (isTrackingEnabled()) {
      globalAnalyticsRef.current.identify(email, {
        email,
        name,
        ...analyticsProperties,
      });
    } else {
      // eslint-disable-next-line no-console
      console.log("Identify:", {
        email,
        name,
        ...analyticsProperties,
      });
    }
  }
}

export { trackEvent, generateAnalyticsProperties, identifyUser };
